import { Component, OnInit, ViewChild, AfterViewInit, OnDestroy, Input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { WizardService, NavigateArgs } from 'src/app/wizard-bar/services/wizard.service';
import { of, Observable, forkJoin, Subscription } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { QuoteRequestService } from '@app/shared/services/quote-request.service';
import { CustomService } from 'src/app/custom/services/custom.service';
import { UdFile } from 'src/app/filemanagement/UdFile';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { ValidationMessageCode } from 'src/app/custom/models/validation-message-codes';
import { ConfirmComponent } from 'src/app/shared/components/confirm/confirm.component';
import { FixDataComponent } from '../../components/fix-data/fix-data-component';
import { DateConstants } from '@app/constants/date-constants';
import { Store } from '@ngrx/store';
import { UtilityHubIdentity } from '@auth/models/utility-hub-identity';
import { QuoteRequestState } from '@app/shared';
import { CommodityType } from '@app/shared/enums/commodity-type';
import { CustomConstants } from '@app/custom/models/custom-constants';
import { SalesType } from '@app/shared/enums/sales-type';
import { AppState } from '@app/state/app.state';
import { salesTypeSelector, utilityUOMSelector } from '@utility-information/state/selectors/utility-information.selectors';
import { isReadOnlySelector, loggedInUserSelector, loggedInUtilityHubIdentitySelector } from '@app/state/app.selectors';
import { SetQuote } from '@quoting/state/actions/quoting-module.actions';

export interface FileData {
  utilityAccountNumber: string;
  startDate: Date;
  endDate: Date;
  meterReadings: any;
  uploadConsumption?: number;
  days: number;
  blanks: number;
  file: UdFile;
  zeroes: number;
  validationMessage: string;
  valid: boolean;
  validatorResults: ValidationResult[];
}

export interface ValidationResult {
  name: string;
  actual: any;
  criteria: any;
  valid: boolean;
}

export interface UtilityAccountValidationResults {
  utilityAccountNumber: string;
  consumptionDataValidationResults: ValidationResult[];
  startDate: any;
  endDate: any;
  meterReadings: number;
  uploadConsumption?: number;
  totalDays?: number;
  isForecasted: boolean;
  file: UdFile;
}

@Component({
  selector: 'app-custom-file-validation',
  templateUrl: './custom-file-validation.component.html',
  styleUrls: ['./custom-file-validation.component.scss'],
})
export class CustomFileValidationComponent implements OnInit, AfterViewInit, OnDestroy {
  public Commodity = CommodityType;
  public quoteRequestId: any;
  public dateFormat = DateConstants.LOCALE_FORMAT_SHORT();

  @ViewChild(MatSort) sort: MatSort;

  @Input() pricingMethod: string;

  displayedColumns = [
    'utilityAccountNumber',
    'startDate',
    'endDate',
    'meterReadings',
    'uploadConsumption',
    'days',
    'blanks',
    'fileName',
    'info',
    'fix',
    'remove',
  ];
  minDataDays = 60;
  quoteRequestState: QuoteRequestState;
  utilityAccounts: UtilityAccountValidationResults[];
  fileData: FileData[] = [];
  dataSource = new MatTableDataSource<FileData>(this.fileData);
  zerosCriteria: number;
  gapsCriteria: number;
  userIdentity: UtilityHubIdentity;
  routeSubscription$: Subscription;
  selectLoggedInUserSubscription$: Subscription;
  selectLoggedInUtilityHubIdentitySubscription$: Subscription;
  loadSubscription$: Subscription;
  dialogAfterClosedSubscription$: Subscription;
  deleteUtilityAccountAfterClosedSubscription$: Subscription;
  salesType$: Observable<string>;
  utilityUOM$: Observable<string>;
  isReadOnly$: Subscription;
  isReadOnly: boolean;
  forcastAllDiagClose$: Subscription

  constructor(
    private route: ActivatedRoute,
    private wizardService: WizardService,
    private quoteRequestService: QuoteRequestService,
    private customQuoteService: CustomService,
    protected dialog: MatDialog,
    private store: Store<AppState>,
  ) {
    this.salesType$ = store.select(salesTypeSelector);
    this.utilityUOM$ = this.store.select(utilityUOMSelector);
  }

  ngOnDestroy(): void {
    this.routeSubscription$?.unsubscribe();
    this.selectLoggedInUserSubscription$?.unsubscribe();
    this.selectLoggedInUtilityHubIdentitySubscription$.unsubscribe();
    this.loadSubscription$?.unsubscribe();
    this.deleteUtilityAccountAfterClosedSubscription$?.unsubscribe();
    this.dialogAfterClosedSubscription$?.unsubscribe();
    this.isReadOnly$?.unsubscribe();
    this.forcastAllDiagClose$?.unsubscribe();
  }

  ngOnInit(): void {
    this.wizardService.bindStep(this.route, (args) => this.save(args));
    this.routeSubscription$ = this.route.params.subscribe((x) => {
      this.quoteRequestId = null;
      if (!isNaN(x.quoteRequestId)) {
        this.quoteRequestId = Number(x.quoteRequestId);
        this.load();
      }
    });

    this.selectLoggedInUserSubscription$ = this.store.select(loggedInUserSelector).subscribe((user) => {
      if (user && !user.expired) {
        this.selectLoggedInUtilityHubIdentitySubscription$ = this.store
          .select(loggedInUtilityHubIdentitySelector)
          .subscribe((uhUser) => {
            this.userIdentity = uhUser;
          });
      }
    });

    this.isReadOnly$ = this.store.select(isReadOnlySelector).subscribe((isReadOnly) => {
      this.isReadOnly = isReadOnly;
    });
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
  }

  mapUtilityAccountsToFileValidationTable() {
    this.fileData = [];
    let validatorResults: ValidationResult[] = [];

    this.utilityAccounts.forEach((element) => {
      const totalDays = element.totalDays;
      let blankDays = 0;
      let zeroDays = 0;
      const forecasted = element.isForecasted;

      element.consumptionDataValidationResults.forEach((val) => {
        if (val.name === 'MinimumDataValidator') {
          // We need one for between min data and 365 days.
          validatorResults.push({
            ...val,
            valid: totalDays >= 365 || (totalDays >= val.criteria && forecasted),
            name: 'ForecastRequiredValidator',
          });
        }
        if (val.name === 'ZeroDataValidator') {
          zeroDays += val.actual;
        }
        if (val.name === 'GapsValidator') {
          blankDays += val.actual;
        }

        validatorResults.push(val);
      });

      this.salesType$.subscribe(salesType => {
        const displayData: FileData = {
          utilityAccountNumber: element.utilityAccountNumber,
          startDate: element.startDate ?? '',
          endDate: element.endDate ?? '',
          meterReadings: element.meterReadings ?? '',
          uploadConsumption: element.uploadConsumption,
          days: totalDays,
          blanks: blankDays,
          zeroes: zeroDays,
          file: element.file,
          valid: salesType === SalesType.renewal ? true : validatorResults.every((x) => x.valid),
          validatorResults,
          validationMessage: '',
        };
       validatorResults = [];

       displayData.validationMessage = this.getValidationMessage(displayData);

       this.fileData.push(displayData);
     }).unsubscribe();
   });
  }

  getValidationMessage(displayData: FileData): string {
    let messageCodes: ValidationMessageCode[] = [];
    if (!displayData.file) {
      messageCodes.push(ValidationMessageCode.NO_DATA);
      return messageCodes.join('\n');
    }

    displayData.validatorResults.forEach((val) => {
      if (val.name === 'FutureDateValidator' && !val.valid) {
        messageCodes.push(ValidationMessageCode.FUTURE_DATE);
      }
      //# TODO the validator names here show the same message, will need to rename 60 and 365 validation message throughout the app.
      if ((val.name === 'ForecastRequiredValidator' || val.name === 'MinimumDataValidator') && !val.valid) {
        if (!messageCodes.includes(ValidationMessageCode.LESS_THAN_60_DAYS)) {
          messageCodes.push(ValidationMessageCode.LESS_THAN_60_DAYS);
        }
      }
      if (val.name === 'GapsValidator' && !val.valid) {
        messageCodes.push(ValidationMessageCode.TOO_MANY_GAPS);
      }
      if (val.name === 'MaximumDataAgeValidator' && !val.valid) {
        messageCodes.push(ValidationMessageCode.DATA_TOO_OLD);
      }
      if (val.name === 'OverlappingDateValidator' && !val.valid) {
        messageCodes.push(ValidationMessageCode.TOO_MANY_OVERLAPPING_DATES);
      }
      if (val.name === 'GapsBetweenDatesValidator' && !val.valid) {
        messageCodes.push(ValidationMessageCode.GAPS_EXIST);
      }
      if (val.name === 'ZeroDataValidator' && !val.valid) {
        messageCodes.push(ValidationMessageCode.TOO_MANY_ZEROS);
      }
      if (val.name == 'MaximumNumberOfDaysInMeterReadValidator' && !val.valid) {
        messageCodes.push(ValidationMessageCode.METERREAD_DAYS_OVER);
      }
    });

    const msg = messageCodes.join('\n');

    return msg;
  }

  load() {
    this.loadSubscription$ = forkJoin({
      state: this.quoteRequestService.getQuoteRequestState(this.quoteRequestId),
      utilityAccounts: this.customQuoteService.getFileValidation(this.quoteRequestId),
    }).subscribe((res) => {
      this.quoteRequestState = res.state;
      this.utilityAccounts = res.utilityAccounts;

      this.mapUtilityAccountsToFileValidationTable();

      this.dataSource = new MatTableDataSource(this.fileData);
      this.dataSource.sort = this.sort;
      console.log(JSON.stringify(res.utilityAccounts));
      this.store.dispatch(new SetQuote(res.state, this.userIdentity, res.state.hasIDontKnows));
    });
  }

  deleteUtilityAccount(utilityAccountNumber: string) {
    const dialogRef = this.dialog.open(ConfirmComponent, {
      width: '25%',
      data: {
        title: 'Delete Utility Account Number',
        message: 'Are you sure you want to delete this Utility Account Number and all data associated with it?',
        primaryButtonText: 'Delete',
      },
    });

    this.deleteUtilityAccountAfterClosedSubscription$ = dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        const warningDialogRef = this.wizardService.resolveExpireQuoteWarningDialog();
        if (warningDialogRef.required) {
          this.dialogAfterClosedSubscription$ = warningDialogRef.dialogRef.afterClosed().subscribe((cfrmWarning) => {
            if (cfrmWarning) {
              this.customQuoteService.deleteUtilityAccountNumber(this.quoteRequestId, utilityAccountNumber).subscribe(() => {
                this.load();
              });
            }
          });
        }
      }
    });
  }

  public showForcastAllDialog() {
    const dialogRef = this.dialog.open(ConfirmComponent, {
      width: '30%',
      data: {
        title: 'Are you sure you want to forecast missing data for all the accounts?',
        message:
          'All the utility accounts that have less than 365 days of usage data will have missing data forecasted.  Are you sure you want to continue?',
        primaryButtonText: 'Confirm Forecast All',
      },
    });

   this.forcastAllDiagClose$ = dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed)
        this.forcastAll();
    });
  }

  public forcastRequired(): boolean {
    return this.dataSource.filteredData.some(file => !file.valid);
  }

  private forcastAll() {
    const observables = this.dataSource.filteredData.filter(file => !file.valid).map(file => 
      this.customQuoteService.setForecasted(this.quoteRequestState.quoteRequestId, file.utilityAccountNumber, true)
    );

    forkJoin(observables).subscribe({
      complete: () => {
        this.load();
      }
    });
  }

  public showFixDialog(data: FileData) {
    const parserNameValue = this.quoteRequestState.commodity == CommodityType.Gas ? 'GasFileParser' : 'StandardScalarFileParser';
    const dialogRef = this.dialog.open(FixDataComponent, {
      width: '40%',
      data: {
        pricingMethod: this.pricingMethod,
        quoteRequestId: this.quoteRequestId,
        utilityAccountNumber: data.utilityAccountNumber,
        validationMessage: data.validationMessage,
        parseRequestData: {
          parserName: parserNameValue,
          quoteRequestId: this.quoteRequestId,
          utilityAccountNumber: data.utilityAccountNumber,
        },
      },
    });

    dialogRef.afterClosed().subscribe(() => {
      this.load();
    });
  }

  public save(args: NavigateArgs): Observable<any> {
    const data = this.dataSource.data;
    if (data.every((x) => x.valid)) {
      return of(true);
    } else {
      if ((args.direction && args.direction < 0) || this.wizardService.clickedStepIsPreviousStep(CustomConstants.wizardSteps, args.step)) {
        return of(true);
      } else {
        return of(false);
      }
    }
  }

  public next() {
    this.wizardService.next();
  }

  public back() {
    this.wizardService.previous();
  }
}
